import React from 'react';
import { Box, Button, Typography, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { markLessonComplete } from '../../services/lessonService';
import { useNotification } from '../../context/NotificationContext';

function Navigation({ lesson }) {
    const { showNotification } = useNotification();

    if (!lesson) return null; // Ensure lesson exists before rendering
    const { previousLesson, course, title, description } = lesson;

    const handleMarkComplete = async () => {
        const result = await markLessonComplete(lesson._id);

        if (result.success) {
            const { progress, nextLesson } = result.data;
            if (!progress.completed && nextLesson) {
                window.location.assign(`/course/${lesson.course.slug}/lesson/${nextLesson._id}`);
            } else if (progress.completed) {
                showNotification("Congratulations! You have completed the course.", "success");
                window.location.assign(`/course/${lesson.course.slug}/completed`);
            } else {
                showNotification("Lesson not available. Please try again.", "error");
            }
        } else {
            showNotification("An error occured. Please try again.", "error");
        }
    };

    return (
        <Box
            sx={{
                px: 2, // Padding for responsiveness
                mb: 2, // Margin below the component
                textAlign: 'center', // Center-align content
            }}
        >
            {/* Navigation Buttons */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: previousLesson ? 'space-between' : 'flex-end',
                    // justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2, md: 4 }, // Adjust gap between buttons
                    mb: 2, // Space below buttons
                }}
            >

                {/* Previous Lesson Button */}
                {previousLesson && (
                    <Button
                        variant="outlined"
                        onClick={() => window.location.assign(`/course/${lesson.course.slug}/lesson/${previousLesson._id}`)}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            textTransform: 'none',
                            fontSize: { xs: '0.8rem', sm: '0.8rem', md: '0.9rem', lg: '1rem' }, // Adjust size dynamically
                            fontWeight: 600,
                            borderRadius: '30px',
                            px: { xs: 1.5, sm: 2, md: 2.5 },
                            py: { xs: 0.8, sm: 1, md: 1.2 },
                            color: 'text.primary',
                            borderColor: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                borderColor: 'primary.main',
                            },
                            transition: 'all 0.3s ease', // Smooth hover effect
                        }}
                    >
                        {/* Change text dynamically based on screen size */}
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Previous Lesson
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Previous
                        </Box>
                    </Button>
                )}

                {/* Mark as Complete Button */}
                <Button
                    variant="contained"
                    onClick={handleMarkComplete}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                        textTransform: 'none',
                        fontSize: { xs: '0.8rem', sm: '0.8rem', md: '0.9rem', lg: '1rem' }, // Adjust size dynamically
                        fontWeight: 600,
                        borderRadius: '30px',
                        px: { xs: 1.5, sm: 2, md: 2.5 },
                        py: { xs: 0.8, sm: 1, md: 1.2 },
                        backgroundColor: 'primary.main',
                        color: 'common.white',
                        '&:hover': {
                            backgroundColor: 'primary.dark',
                        },
                        transition: 'all 0.3s ease', // Smooth hover effect
                    }}
                >
                    {/* Change text dynamically based on screen size */}
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Mark as Complete
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Complete
                    </Box>
                </Button>

            </Box>

            {/* Divider */}
            <Divider sx={{ mb: 2 }} />

            {/* Heading and Description */}
            {/* Heading and Description */}
            <Box sx={{ mt: 2, textAlign: 'left' }}>
                {/* Lesson Title */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        color: '#000',
                        mb: 1,
                    }}
                >
                    {lesson.title}
                </Typography>

                {/* Module and Lesson Number */}
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        color: '#000',
                        mb: 1.5,
                    }}
                >
                    {(() => {
                        const module = lesson.courseOutline.find(m => m._id === lesson.module);
                        const moduleOrder = module?.order ?? '-';
                        const lessonOrder = module?.lessons?.find(l => l._id === lesson._id)?.order ?? '-';
                        return `Module ${moduleOrder} - Lesson ${lessonOrder}`;
                        // Try •, ●, or ◉ and see which fits your UI best.
                    })()}
                </Typography>

                {/* Lesson Description */}
                <Typography
                    variant="body1"
                    sx={{
                        color: '#000',
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        lineHeight: 1.7,
                        maxWidth: '90%',
                    }}
                >
                    {lesson.description}
                </Typography>
            </Box>

            {/* END */}

        </Box>
    );
}

export default Navigation;
